import { motion } from "motion/react";
import { Link } from "react-router";
import { useState } from "react";
import { ProjectModal } from "./ProjectModal";
import { 
  Building2, Stethoscope, Landmark, Truck, GraduationCap, 
  ShoppingCart, Briefcase, factory, Home, Utensils, Zap, Globe 
} from "lucide-react";

const industries = [
  {
    id: "fintech",
    icon: <Landmark size={32} />,
    title: "FinTech & Banking",
    description: "Secure, compliant, and high-performance financial solutions. We build trading platforms, digital wallets, and banking infrastructure.",
    challenges: ["Security Compliance", "Real-time Transactions", "Legacy Integration"],
    solutions: ["Blockchain Integration", "AI Fraud Detection", "Open Banking APIs"]
  },
  {
    id: "healthcare",
    icon: <Stethoscope size={32} />,
    title: "Healthcare & MedTech",
    description: "Digital health solutions that improve patient care and operational efficiency. HIPAA-compliant software for hospitals and startups.",
    challenges: ["Data Privacy (HIPAA)", "Interoperability", "Patient Engagement"],
    solutions: ["Telemedicine Apps", "EHR Systems", "IoT Health Monitoring"]
  },
  {
    id: "logistics",
    icon: <Truck size={32} />,
    title: "Logistics & Supply Chain",
    description: "End-to-end visibility and automation for supply chains. Optimize routes, track assets, and manage inventory in real-time.",
    challenges: ["Route Optimization", "Inventory Tracking", "Last-mile Delivery"],
    solutions: ["Fleet Management", "Warehouse Automation", "Predictive Logistics"]
  },
  {
    id: "education",
    icon: <GraduationCap size={32} />,
    title: "EdTech & E-Learning",
    description: "Interactive learning platforms that engage students and empower educators. From LMS to gamified learning experiences.",
    challenges: ["Student Engagement", "Remote Access", "Personalized Learning"],
    solutions: ["LMS Development", "Virtual Classrooms", "AI Tutors"]
  },
  {
    id: "retail",
    icon: <ShoppingCart size={32} />,
    title: "Retail & E-Commerce",
    description: "Omnichannel retail solutions that drive sales and loyalty. Seamless shopping experiences across web, mobile, and in-store.",
    challenges: ["Customer Retention", "Inventory Sync", "Personalization"],
    solutions: ["Headless Commerce", "AR Try-ons", "Loyalty Programs"]
  },
  {
    id: "manufacturing",
    icon: <Building2 size={32} />,
    title: "Manufacturing & IIoT",
    description: "Smart manufacturing solutions that reduce downtime and increase productivity. Connect machines, data, and people.",
    challenges: ["Equipment Downtime", "Quality Control", "Supply Chain Visibility"],
    solutions: ["Predictive Maintenance", "Digital Twins", "IIoT Dashboards"]
  },
  {
    id: "realestate",
    icon: <Home size={32} />,
    title: "Real Estate & PropTech",
    description: "Modern platforms for buying, selling, and managing properties. Virtual tours, property management systems, and smart building tech.",
    challenges: ["Virtual Viewings", "Transaction Transparency", "Tenant Management"],
    solutions: ["VR Property Tours", "Smart Contracts", "Property Management Software"]
  },
  {
    id: "energy",
    icon: <Zap size={32} />,
    title: "Energy & Utilities",
    description: "Sustainable tech solutions for the energy sector. Grid management, renewable energy tracking, and consumer apps.",
    challenges: ["Grid Modernization", "Energy Efficiency", "Consumer Data"],
    solutions: ["Smart Metering", "Renewable Energy Tracking", "Grid Analytics"]
  },
  {
    id: "hospitality",
    icon: <Utensils size={32} />,
    title: "Hospitality & Travel",
    description: "Digital experiences that delight guests and travelers. Booking engines, property management, and personalized travel apps.",
    challenges: ["Direct Bookings", "Guest Experience", "Operational Efficiency"],
    solutions: ["Booking Engines", "Contactless Check-in", "Travel Itinerary Apps"]
  },
  {
    id: "professional",
    icon: <Briefcase size={32} />,
    title: "Professional Services",
    description: "Streamlined operations for law firms, consultancies, and agencies. Client portals, document automation, and workflow tools.",
    challenges: ["Client Communication", "Document Management", "Billable Hours"],
    solutions: ["Client Portals", "Workflow Automation", "Secure Document Sharing"]
  },
  {
    id: "public",
    icon: <Globe size={32} />,
    title: "Public Sector & GovTech",
    description: "Citizen-centric digital services that are accessible and secure. Modernizing government infrastructure for the digital age.",
    challenges: ["Accessibility", "Data Security", "Legacy Systems"],
    solutions: ["Citizen Portals", "E-Voting Systems", "Smart City Solutions"]
  }
];

export function IndustriesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="pt-20">
      {/* Hero Header */}
      <section className="bg-slate-900 text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-[#FD5750] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-blue-500 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#FD5750] font-bold tracking-widest uppercase mb-4 block">Sectors We Serve</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Industry-Specific <span className="text-[#FD5750]">Innovation</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              We understand the unique challenges of your sector. Our specialized solutions are designed to give you a competitive edge.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl border border-transparent hover:border-[#FD5750]/20 transition-all duration-300 group flex flex-col h-full"
              >
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center text-slate-700 mb-6 group-hover:bg-[#FD5750] group-hover:text-white transition-colors duration-300">
                  {industry.icon}
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-[#FD5750] transition-colors">
                  {industry.title}
                </h3>
                
                <p className="text-slate-600 mb-6 flex-grow leading-relaxed">
                  {industry.description}
                </p>

                <div className="space-y-4 pt-6 border-t border-slate-100">
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Key Challenges</h4>
                    <div className="flex flex-wrap gap-2">
                      {industry.challenges.map((challenge, idx) => (
                        <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          {challenge}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-xs font-bold text-[#FD5750] uppercase tracking-wider mb-2">Our Solutions</h4>
                    <div className="flex flex-wrap gap-2">
                      {industry.solutions.map((solution, idx) => (
                        <span key={idx} className="text-xs bg-orange-50 text-[#FD5750] px-2 py-1 rounded">
                          {solution}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Don't see your industry?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Our technology expertise is adaptable. Talk to us about your specific business needs and we'll design a custom solution for you.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-bold rounded-lg hover:bg-[#FD5750] transition-colors shadow-lg cursor-pointer"
          >
            Discuss Your Project
          </button>
        </div>
      </section>

      <ProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
